<template>
  <div>
    <div class="text-center mb-10">
      <SectionTitle>Nossos Planos</SectionTitle>
      <p class="pricing-subtitle mt-3">
        Implementação gratuita · Suporte humanizado · Garantia de 30 dias
      </p>
    </div>

    <v-row justify="center" align="stretch">

      <!-- ── Plano Essencial ── -->
      <v-col cols="12" sm="6" md="5">
        <div class="plan-card">
          <div class="plan-header">
            <span class="plan-name">Plano Essencial</span>
            <p class="plan-tagline">Tudo que você precisa para começar</p>
          </div>

          <!-- Preço oculto temporariamente (valor original: R$197/mês)
          <div class="plan-price">
            <span class="price-amount">R$197</span>
            <span class="price-period">/mês</span>
          </div>
          -->

          <div class="plan-cta">
            <button class="cta-btn pulse-btn" @click="selectPlan('essencial')">
              Quero o Essencial
            </button>
          </div>

          <v-divider class="plan-divider" />

          <ul class="feature-list">
            <li v-for="(item, i) in essentialFeatures" :key="i" class="feature-item">
              <v-icon size="16" color="#23cf65" class="feature-icon">mdi-check-circle</v-icon>
              <span>{{ item.feature }}</span>
            </li>
          </ul>
        </div>
      </v-col>

      <!-- ── Plano Profissional ── -->
      <v-col cols="12" sm="6" md="5">
        <div class="plan-card plan-card--featured">

          <div class="featured-badge">
            <v-icon small class="mr-1">mdi-fire</v-icon>Mais vendido
          </div>

          <div class="plan-header">
            <span class="plan-name">Plano Profissional</span>
            <p class="plan-tagline">Para quem quer escalar o negócio</p>
          </div>

          <!-- Preço oculto temporariamente (valor original: R$259/mês)
          <div class="plan-price">
            <span class="price-amount">R$259</span>
            <span class="price-period">/mês</span>
          </div>
          -->

          <div class="plan-cta">
            <button class="cta-btn cta-btn--featured pulse-btn" @click="selectPlan('profissional')">
              Quero o Profissional
            </button>
          </div>

          <v-divider class="plan-divider" />

          <ul class="feature-list">
            <li
              v-for="(item, i) in professionalFeatures"
              :key="i"
              class="feature-item"
              :class="{ 'feature-item--highlight': item.highlight }"
            >
              <v-icon
                size="16"
                :color="item.highlight ? '#23cf65' : '#23cf65'"
                class="feature-icon"
              >{{ item.highlight ? 'mdi-check-all' : 'mdi-check-circle' }}</v-icon>
              <span>{{ item.feature }}</span>
            </li>
          </ul>

        </div>
      </v-col>

    </v-row>

    <!-- Garantia -->
    <div class="guarantee-row mt-8">
      <v-icon small color="success" class="mr-2">mdi-shield-check</v-icon>
      <span>Garantia de <strong>30 dias</strong> — satisfação garantida ou devolvemos seu investimento</span>
    </div>
  </div>
</template>

<script>
import SectionTitle from '@/components/SectionTitle.vue';
import { trackClick } from '@/utils/lpTracker';

export default {
  name: 'PricingSection',

  components: { SectionTitle },

  data: () => ({
    essentialFeatures: [
      { feature: 'Sistema Completo: Delivery, Balcão, Mesa' },
      { feature: 'Cardápio digital' },
      { feature: 'Gestor de pedidos' },
      { feature: 'Impressão automática' },
      { feature: 'PDV, QRCode' },
      { feature: 'App garçom' },
      { feature: 'Robô de atendimento' },
      { feature: 'Venda sugestiva' },
      { feature: 'Cupons de desconto' },
      { feature: 'Sem taxas e limite de pedidos' },
      { feature: 'Relatórios completos' },
      { feature: 'Acompanhamento personalizado' },
      { feature: 'Suporte via whatsapp' },
    ],
    professionalFeatures: [
      { feature: 'Tudo do Plano Essencial', highlight: true },
      { feature: 'Controle de caixa' },
      { feature: 'Controle de estoque' },
      { feature: 'Ficha Técnica' },
      { feature: 'Pix automatizado' },
      { feature: 'Contas a pagar/receber' },
      { feature: 'Monitor de Preparo (KDS)' },
      { feature: 'Roteirizador de entregas' },
      { feature: 'Programa de fidelidade' },
      { feature: 'Atendente 24/7 com IA' },
      { feature: 'Disparos com API Oficial do Whatsapp' },
      { feature: 'Suporte prioritário' },
    ],
  }),

  methods: {
    // Preços ocultos temporariamente — valores originais: essencial=197, profissional=259
    // Fluxo atual: CTAs levam ao /whatsapp para qualificação manual (sem passar por checkout/pagamento)
    selectPlan(id) {
      const labels = { essencial: 'Plano Essencial', profissional: 'Plano Profissional' };
      trackClick(`Pricing - ${labels[id]}`);
      this.$router.push({ path: '/whatsapp' });
    },
  },
};
</script>

<style scoped lang="scss">
/* Subtítulo da seção */
.pricing-subtitle {
  font-size: 15px;
  color: var(--text-muted);
}

/* ── Card base ── */
.plan-card {
  position: relative;
  background: var(--card-bg);
  border: 1px solid var(--card-border);
  border-radius: 24px;
  padding: 36px 32px 32px;
  height: 100%;
  display: flex;
  flex-direction: column;
  transition: box-shadow 0.25s;

  @media (max-width: 600px) {
    padding: 28px 20px 24px;
  }
}

/* ── Card em destaque ── */
.plan-card--featured {
  border: 2px solid #f40612;
  box-shadow: 0 0 0 1px rgba(244, 6, 18, 0.15),
              0 16px 48px rgba(244, 6, 18, 0.1);
}

/* Badge "Mais vendido" */
.featured-badge {
  position: absolute;
  top: -16px;
  left: 50%;
  transform: translateX(-50%);
  display: inline-flex;
  align-items: center;
  padding: 5px 16px;
  border-radius: 999px;
  background: #f40612;
  color: #fff;
  font-size: 12px;
  font-weight: 700;
  letter-spacing: 0.04em;
  white-space: nowrap;
  box-shadow: 0 4px 14px rgba(244, 6, 18, 0.4);
}

/* ── Cabeçalho do plano ── */
.plan-header {
  text-align: center;
  margin-bottom: 20px;
}

.plan-name {
  display: block;
  font-size: 20px;
  font-weight: 700;
  color: var(--text-primary);
  letter-spacing: -0.01em;
}

.plan-tagline {
  margin: 6px 0 0;
  font-size: 13px;
  color: var(--text-muted);
  line-height: 1.5;
}

/* ── Preço ── */
.plan-price {
  display: flex;
  align-items: baseline;
  justify-content: center;
  gap: 4px;
  margin-bottom: 20px;
}

.price-amount {
  font-size: 48px;
  font-weight: 800;
  color: var(--text-primary);
  letter-spacing: -0.02em;
  line-height: 1;
}

.price-period {
  font-size: 16px;
  color: var(--text-muted);
  font-weight: 500;
}

/* ── Botão CTA ── */
.plan-cta {
  margin-bottom: 24px;
}

.cta-btn {
  display: block;
  width: 100%;
  padding: 14px 0;
  border-radius: 12px;
  border: none;
  background: #23cf65;
  color: #fff;
  font-size: 15px;
  font-weight: 700;
  cursor: pointer;
  letter-spacing: 0.01em;
  transition: opacity 0.2s, transform 0.2s;

  &:hover {
    opacity: 0.9;
    transform: translateY(-1px);
  }

  &--featured {
    background: linear-gradient(135deg, #23cf65 0%, #1aaa52 100%);
    box-shadow: 0 6px 20px rgba(35, 207, 101, 0.35);
  }
}

@keyframes pulse {
  0%, 100% { box-shadow: 0 0 0 0 rgba(35, 207, 101, 0.45); }
  50%       { box-shadow: 0 0 0 8px rgba(35, 207, 101, 0); }
}

.pulse-btn {
  animation: pulse 2.2s ease-in-out infinite;
}

/* ── Divisor ── */
.plan-divider {
  border-color: var(--card-border) !important;
  margin-bottom: 20px;
}

/* ── Lista de features ── */
.feature-list {
  list-style: none;
  padding: 0;
  margin: 0;
  display: flex;
  flex-direction: column;
  gap: 10px;
}

.feature-item {
  display: flex;
  align-items: center;
  gap: 10px;
  font-size: 14px;
  color: var(--text-secondary);
  line-height: 1.4;

  &--highlight {
    background: rgba(35, 207, 101, 0.09);
    border: 1px solid rgba(35, 207, 101, 0.28);
    border-radius: 8px;
    padding: 7px 10px;
    font-weight: 700;
    color: var(--text-primary);
    letter-spacing: 0.01em;
  }
}

.feature-icon {
  flex-shrink: 0;
}

/* ── Faixa de garantia ── */
.guarantee-row {
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 14px;
  color: var(--text-muted);
  text-align: center;
}
</style>
